import { useState, useCallback } from 'react';

import { isFeatureEnabled } from '$shared/feature-toggle/is-feature-enabled';
import i18next from '$shared/i18n/i18n';

import { ChatService } from './chat-service';
import { ChatMessage } from './types';

export function useChatBot() {
  const useStrandsAgent = isFeatureEnabled('ESEVNLS_4737_STRANDS_AGENT_CHATBOT');
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      text: i18next.t('translation:chatbot.initialBotMessage'),
      isInitial: true,
      type: 'assistant',
      timestamp: new Date(),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatService] = useState(() => new ChatService());

  const sendMessageLegacy = useCallback(
    async (text: string) => {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          text: text.trim(),
          type: 'user',
          timestamp: new Date(),
        },
      ]);
      setIsLoading(true);

      try {
        const response = await chatService.sendMessage(text.trim());

        setMessages((prev) => [
          ...prev,
          {
            id: response.messageId,
            text: response.response,
            type: 'assistant',
            timestamp: new Date(),
          },
        ]);
      } catch (error) {
        // eslint-disable-next-line no-console -- Logging error for debugging chat service failures
        console.error('Failed to send chat message:', error);
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            text: i18next.t('translation:chatbot.connectionError'),
            type: 'assistant',
            timestamp: new Date(),
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [chatService],
  );

  const sendMessageStreaming = useCallback(
    async (text: string) => {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          text: text.trim(),
          type: 'user',
          timestamp: new Date(),
        },
      ]);
      setIsLoading(true);

      const placeholderId = (Date.now() + 1).toString();
      setMessages((prev) => [
        ...prev,
        { id: placeholderId, text: '', type: 'assistant', timestamp: new Date() },
      ]);

      try {
        await chatService.sendMessageStreaming(text.trim(), {
          onToken: (token) => {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === placeholderId ? { ...msg, text: msg.text + token } : msg,
              ),
            );
          },
          onDone: (_, messageId) => {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === placeholderId ? { ...msg, id: messageId } : msg,
              ),
            );
          },
          onError: (error) => {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === placeholderId
                  ? {
                      ...msg,
                      text: error || i18next.t('translation:chatbot.connectionError'),
                    }
                  : msg,
              ),
            );
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console -- Logging error for debugging chat service failures
        console.error('Failed to send chat message:', error);
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === placeholderId
              ? { ...msg, text: i18next.t('translation:chatbot.connectionError') }
              : msg,
          ),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [chatService],
  );

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isLoading) return;

      if (useStrandsAgent) {
        await sendMessageStreaming(text);
      } else {
        await sendMessageLegacy(text);
      }
    },
    [isLoading, sendMessageStreaming, sendMessageLegacy, useStrandsAgent],
  );

  const giveFeedback = useCallback(
    async (messageId: string, feedback: 'up' | 'down') => {
      const resolvedFeedback =
        messages.find((m) => m.id === messageId)?.feedback === feedback ? null : feedback;
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, feedback: resolvedFeedback } : msg,
        ),
      );
      await chatService.submitFeedback(messageId, resolvedFeedback);
    },
    [chatService, messages],
  );

  const resetChat = useCallback(() => {
    chatService.resetSession();
    setMessages([
      {
        id: '1',
        text: i18next.t('translation:chatbot.initialBotMessage'),
        type: 'assistant',
        isInitial: true,
        timestamp: new Date(),
      },
    ]);
  }, [chatService]);

  return {
    messages,
    isLoading,
    sendMessage,
    giveFeedback,
    resetChat,
  };
}
